import Button from "app/storybookComponents/Button";
import Toggle from "app/components/Toggle";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectAiFeaturesEnabled,
  selectDepartments,
  updateDepartment,
} from "app/containers/AdminConsole/slice";
import { openEditDepartmentModal } from "app/components/Modals/slice";
import {
  selectAllCompanyUsersById,
  selectConfigCatFlag,
  selectIsCurrentUserAdmin,
} from "../Global/slice";
import { CONFIG_CAT_FLAGS } from "app/components/GlobalNavbar/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getWeeklyCheckInSettingDisabledDueToCompanySettingBanner,
  getWeeklyCheckInSettingDisabledDueToCompanyWideSettingBanner,
  isDepartmentWeeklyCheckInSettingLocked,
} from "../WeeklyCheckInMonitor/helpers";
import {
  getCompanyWeeklyCheckInCustomList,
  getWeeklyCheckInCompanySettings,
  selectGetCompanyWeeklyCheckInCustomListStatus,
  selectGetWeeklyCheckInCompanySettingsStatus,
  selectWeeklyCheckInCompanySettings,
  setSampleWeeklyCheckInEmailReportModalState,
} from "../WeeklyCheckInMonitor/slice";
import { useEffect, useState } from "react";
import ToggleCheckInFeatureWarningModal from "../WeeklyCheckInMonitor/Modals/ToggleCheckInFeatureWarningModal";
import { Link } from "react-router-dom";
import { WEEKLY_CHECK_IN_FEATURE_NAME } from "../WeeklyCheckInMonitor/constants";
import Loading from "app/storybookComponents/Loading";
import { getUserName } from "utils/helperFunctions";

interface Props {
  departmentId: number;
}

const DepartmentSettings = ({ departmentId }: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const isUserAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const departments = useAppSelector(selectDepartments);
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const areAIFeaturesEnabled = useAppSelector(selectAiFeaturesEnabled);
  const weeklyCheckInConfigCatFlag = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.weeklyCheckIn.savedFlagName)
  );
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const getWeeklyCheckInCompanySettingsStatus = useAppSelector(
    selectGetWeeklyCheckInCompanySettingsStatus
  );
  const getCompanyWeeklyCheckInCustomListStatus = useAppSelector(
    selectGetCompanyWeeklyCheckInCustomListStatus
  );

  const [toggleCheckInFeatureModal, setToggleCheckInFeatureModal] = useState<{
    isOpen: boolean;
    changingTo?: boolean;
  }>({
    isOpen: false,
  });

  useEffect(() => {
    dispatch(getWeeklyCheckInCompanySettings());
    dispatch(getCompanyWeeklyCheckInCustomList());
  }, [dispatch]);

  const departmentInfo = departments[departmentId];
  const getDepartmentNameSetting = () => (
    <div className="setting-row">
      <div className="setting-row-description">
        <h3>Department</h3>
        <p>{departmentInfo?.name}</p>
      </div>
      <div className="setting-row-action">
        <Button
          variant="secondary-blue"
          onClick={() => dispatch(openEditDepartmentModal({ departmentId }))}
        >
          Edit department name
        </Button>
      </div>
    </div>
  );

  const getWeeklyCheckInSettingWarningBanner = () => {
    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany
    ) {
      return getWeeklyCheckInSettingDisabledDueToCompanyWideSettingBanner(
        !!isUserAdmin,
        "department"
      );
    }

    if (departmentInfo?.weeklyCheckInsEnabledByUserAccountId) {
      return (
        <div className="gold warning-banner">
          <div className="row-gap-12px justify-content-between align-items-center w-100">
            <p>
              <FontAwesomeIcon
                icon={["far", "lock-keyhole"]}
                className="me-2"
              />
              {WEEKLY_CHECK_IN_FEATURE_NAME}s have been <b>enabled</b> for
              everyone in this department by{" "}
              {getUserName(
                usersById[departmentInfo?.weeklyCheckInsEnabledByUserAccountId]
              ) ?? "an admin"}
              .{" "}
              {isUserAdmin
                ? `You can modify ${WEEKLY_CHECK_IN_FEATURE_NAME}s settings in organization settings.`
                : "Please reach out to an admin to change these settings."}{" "}
            </p>
            {!!isUserAdmin && (
              <p className="nowrap">
                <Link to="/AdminConsole/Organization%20Settings/Check-Ins">
                  Go to organization settings{" "}
                  <FontAwesomeIcon icon="arrow-right" className="ms-2" />
                </Link>
              </p>
            )}
          </div>
        </div>
      );
    }

    return getWeeklyCheckInSettingDisabledDueToCompanySettingBanner(
      !!isUserAdmin
    );
  };

  const getWeeklyCheckInSetting = () => {
    if (!areAIFeaturesEnabled || !weeklyCheckInConfigCatFlag) return null;
    if (
      getWeeklyCheckInCompanySettingsStatus === "loading" ||
      getCompanyWeeklyCheckInCustomListStatus === "loading"
    ) {
      return <Loading size={25} />;
    }

    const departmentHasCoachBoCheckInsEnabled =
      departmentInfo?.hasCoachBoWeeklyCheckInsEnabled ||
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany; // first check if department has its own setting, if not, use company setting

    const isSettingLocked = isDepartmentWeeklyCheckInSettingLocked(
      weeklyCheckInCompanySettings,
      departmentInfo,
      !!isUserAdmin,
      usersById
    );

    return (
      <>
        <hr className="m-0" />
        <div className="setting-row">
          <div className="setting-row-description">
            <h3>Enable {WEEKLY_CHECK_IN_FEATURE_NAME}s</h3>
            <p>
              Everyone on teams in this department will receive an email from
              Coach Bo to check on work progress, share how they’re feeling, and
              report any successes or challenges at the end of each check-in period.
              <span
                className="blue-link"
                onClick={() => {
                  dispatch(
                    setSampleWeeklyCheckInEmailReportModalState({
                      isOpen: true,
                      type: "check-in-email",
                    })
                  );
                }}
              >
                Preview email
              </span>
            </p>
          </div>
          <div className="setting-row-action">
            {isSettingLocked ? (
              <FontAwesomeIcon
                icon={["far", "lock-keyhole"]}
                className="me-2"
              />
            ) : null}
            <Toggle
              isOn={!!departmentHasCoachBoCheckInsEnabled}
              handleToggle={() => {
                setToggleCheckInFeatureModal({
                  isOpen: true,
                  changingTo: !departmentHasCoachBoCheckInsEnabled,
                });
              }}
              disabled={isSettingLocked}
            />
          </div>
        </div>

        {isSettingLocked ? getWeeklyCheckInSettingWarningBanner() : null}
      </>
    );
  };

  return (
    <>
      <ToggleCheckInFeatureWarningModal
        isOpen={toggleCheckInFeatureModal.isOpen}
        onHide={() => {
          setToggleCheckInFeatureModal({
            isOpen: false,
          });
        }}
        onConfirmClick={async () => {
          await dispatch(
            updateDepartment({
              hasCoachBoWeeklyCheckInsEnabled:
                toggleCheckInFeatureModal.changingTo ? 1 : 0,
              departmentId,
            })
          );
          dispatch(getCompanyWeeklyCheckInCustomList({ forceRefresh: true }));
          setToggleCheckInFeatureModal({
            isOpen: false,
          });
        }}
        departmentId={departmentId}
        valueChangingTo={toggleCheckInFeatureModal.changingTo}
      />
      <div className="column-gap-20px">
        {getDepartmentNameSetting()}
        {getWeeklyCheckInSetting()}
      </div>
    </>
  );
};

export default DepartmentSettings;
